import { User } from '@/models/Users'
import { useCurrentUserOrgs } from '@/utils/hooks/orgs'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { UUID } from 'crypto'
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'

import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, Link } from 'react-router-dom'
import { loadProgress } from '@/pages/onboarding/api/onboardingProgressAPI'
import { useStagedContentGuard } from './StagedContentGuardProvider'

const STAGED_CONTENT_ORG_SWITCH_CONFIRM =
  "You have unsaved changes, or a file or website that hasn't been uploaded yet. Switching organisations will lose them. Are you sure you want to continue?"

interface SelectOrgProps {
  token: string | null
  user: User | null
  userOrgs: UUID[] | null
}

export const SelectOrg = ({ token, user, userOrgs }: SelectOrgProps) => {
  // Read from UserProvider's context instead of calling useCurrentUser()
  // again here — this component previously instantiated its own, entirely
  // separate useMutation() for updateUser. Since that mutation lived only
  // as long as SelectOrg itself, closing the mobile nav menu mid-switch
  // (which unmounts SelectOrg — see MobileNav's menuOpen conditional
  // render) silently dropped the in-flight mutation's onError/onSuccess
  // callbacks. UserProvider's own useCurrentUser() call never unmounts, so
  // reading updateUser from there instead makes the mutation outlive any
  // one caller's mount lifecycle. setSwitchError writes into the same
  // provider - and UserProvider renders the resulting message itself
  // (a Snackbar, not here), so it stays visible even if this component
  // unmounts before the switch settles.
  const { updateUser, setSwitchError } = getCurrentUser()
  const queryClient = useQueryClient()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { orgData } = useCurrentUserOrgs(
    token as string,
    user,
    userOrgs as UUID[]
  )
  const navigate = useNavigate()
  const { hasStagedContent } = useStagedContentGuard()

  // Parents (DesktopNav/MobileNav) already gate rendering SelectOrg on
  // UserProvider's isLoading, so updateUser is only null before the
  // provider's first resolve — defensive, not expected in practice.
  if (!updateUser) {
    return null
  }

  // users can create new orgs if they already have an org
  const canCreateNewOrg = userOrgs && userOrgs.length > 0

  // ensure we have org data that matches the user's orgs
  const hasValidOrgData =
    orgData && orgData.length > 0 && userOrgs && userOrgs.length > 0

  if (hasValidOrgData)
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isMobile ? 'center' : 'flex-start',
          mt: 1,
          mb: 2,
          pl: isMobile ? 0 : 2,
          pb: 2,
        }}
      >
        <FormControl sx={{ minWidth: 200, width: 300 }}>
          <InputLabel id="org-select-label">
            Select Organisation
          </InputLabel>
          <Select
            labelId="org-select-label"
            value={user?.selected_org_uuid || userOrgs?.[0] || ('' as UUID)}
            label="Select Organisation"
            displayEmpty
            onChange={(e) => {
              const newOrgUuid = e.target.value as UUID

              // prevent rapid-fire changes while mutation is in progress
              if (updateUser.isPending) {
                return
              }

              // only update if we have a valid org UUID and it's different from current
              if (
                user &&
                token &&
                newOrgUuid &&
                newOrgUuid !== user.selected_org_uuid
              ) {
                // Guard first: bail before touching anything, so declining
                // doesn't clear a previous error for a switch that then
                // didn't happen.
                if (
                  hasStagedContent &&
                  !window.confirm(STAGED_CONTENT_ORG_SWITCH_CONFIRM)
                ) {
                  return
                }
                setSwitchError(null)
                updateUser.mutate(
                  {
                    token,
                    editedUser: {
                      ...user,
                      selected_org_uuid: newOrgUuid,
                    },
                  },
                  {
                    onSuccess: async () => {
                      // Wait for currentUser refetch to complete so the shell
                      // and GuardedOutlet read the correct selected_org_uuid.
                      // useUpdateUserMutation already triggers invalidation;
                      // this await ensures the refetch finishes before we navigate.
                      await queryClient.invalidateQueries({ queryKey: ['currentUser'] })

                      try {
                        const progress = await loadProgress(token!, newOrgUuid)
                        if (progress && !progress.is_complete) {
                          navigate('/onboarding')
                        } else {
                          navigate('/')
                        }
                      } catch {
                        navigate('/')
                      }

                      // Background refresh for dependent queries
                      queryClient.invalidateQueries({ queryKey: ['fundingNeeds'] })
                      queryClient.invalidateQueries({ queryKey: ['userOrgs'] })
                      queryClient.invalidateQueries({ queryKey: ['currentOrgs'] })
                    },
                    onError: () => {
                      // Most likely #2075's membership race: the FOR UPDATE
                      // lock rejects this switch when the org was removed
                      // (or being removed) concurrently. A silent bounce
                      // home gave no indication the switch failed.
                      setSwitchError(
                        "Couldn't switch organisations — your access to that organisation may have just changed. Please try again."
                      )
                      navigate('/')
                    },
                  }
                )
              }
            }}
            size="small"
          >
            {orgData.map((org) => (
              <MenuItem key={org.uuid} value={org.uuid} sx={{ width: '100%' }}>
                {org.name}
              </MenuItem>
            ))}
            {canCreateNewOrg && (
                <MenuItem
                  component={Link}
                  to="/onboarding?new=true"
                  sx={{
                    width: '100%',
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    },
                  }}
                >
                  <AddIcon sx={{ mr: 0.5, fontSize: '16px' }} />
                  Add another Organisation
                </MenuItem>
              )}
          </Select>
        </FormControl>
      </Box>
    )
}
